import { Button } from '@neo/primitives/button'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Textarea } from '@neo/primitives/textarea'
import { Tooltip } from '@neo/primitives/tooltip'
import EmojiPicker, { type EmojiClickData } from 'emoji-picker-react'
import { ArrowUp, Loader2, Paperclip, Smile } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useFileUpload } from 'v2/shared/hooks/use-file-upload'
import { FlowTypes } from 'v2/types'
import type { RFileUploadResponse } from 'v2/types/api/auth'
import { ConvChannels } from 'v2/types/api/conversations'
import { useStartNodeType } from '../../hooks/use-start-node-type'
import { FlowNodeTypes } from '../../types'
import { useFlowRunStore } from '../store'
import { EMOJI_PICKER_THEME_CLASS_NAME } from './constants'
import { useSendMessage } from './use-send-message'

interface ChatInputProps {
  placeholder?: string
  disabled?: boolean
}

export const ChatInput = ({ placeholder = 'Your message', disabled = false }: ChatInputProps) => {
  const [message, setMessage] = useState('')
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<RFileUploadResponse | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const startType = useStartNodeType()
  const { executionHistory } = useFlowRunStore()
  const currenttExecutingNode = executionHistory.at(-1)
  const { channelName } = useFlowRunStore((state) => state.asyncAgentMeta)
  const { sendMessage, isSending } = useSendMessage()

  const { uploadFile, isUploading } = useFileUpload((file: RFileUploadResponse) => {
    setUploadedFile(file)
  })

  useEffect(() => {
    if (!disabled) {
      requestAnimationFrame(() => {
        textareaRef.current?.focus()
      })
    }
  }, [disabled])

  if (startType === FlowTypes.incoming_call) return null
  if (currenttExecutingNode?.current_node_type === FlowNodeTypes.end_conversation) return null
  if (
    ![FlowNodeTypes.ai_agent_v2, FlowNodeTypes.agent_preset_conversation].includes(
      currenttExecutingNode?.current_node_type as FlowNodeTypes,
    )
  ) {
    // if the current node is not an ai agent, disable the input
    disabled = true
  }

  const handleSendMessage = () => {
    const trimmedMessage = message.trim()
    const hasContent = trimmedMessage || uploadedFile

    if (hasContent && !disabled && !isSending) {
      sendMessage(trimmedMessage || 'Media file', uploadedFile)
      setMessage('')
      setUploadedFile(null)
      setIsEmojiPickerOpen(false)
      textareaRef.current?.focus()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const newText = message.slice(0, start) + emojiData.emoji + message.slice(end)
    setMessage(newText)

    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(start + emojiData.emoji.length, start + emojiData.emoji.length)
    }, 0)
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadFile(file)
      e.target.value = ''
    }
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const handleRemoveFile = () => {
    setUploadedFile(null)
  }

  const isAsync = channelName === ConvChannels.WHATSAPP || channelName === ConvChannels.CHAT

  return (
    <div className="border-t border-border bg-card px-4 py-3 font-inter">
      {/* File preview */}
      {uploadedFile && (
        <div className="mb-2 flex items-center gap-2 p-2 border border-border bg-bg2 rounded-lg">
          <div className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">
            <span className="font-inter text-s-500 text-foreground">{uploadedFile.file_name}</span>
            <span className="font-jetbrains text-xs-400 text-secondary tabular-nums ml-2">
              ({Math.round(uploadedFile.file_size / 1024)} KB)
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRemoveFile}
            className="text-secondary hover:text-status-danger"
            aria-label="Remove attachment"
            data-testid="button-remove-file"
          >
            ×
          </Button>
        </div>
      )}

      {/* Input field */}
      <div className="mb-2">
        <Textarea
          autoExpand
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full border-none max-h-44 resize-none overflow-auto outline-none bg-transparent focus-visible:ring-0 p-0 font-inter text-s-400 text-foreground"
          placeholder={uploadedFile ? 'Add a caption (optional)' : placeholder}
          disabled={disabled}
          rows={1}
          aria-label="Message"
          data-testid="textarea-playground-message"
        />
      </div>

      <div className="flex items-center justify-between">
        {/* Left side actions */}
        <div className="flex items-center gap-2">
          {/* Emoji Picker */}
          <Popover open={isEmojiPickerOpen} onOpenChange={setIsEmojiPickerOpen}>
            <Tooltip content="Insert emoji">
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-md border-st2"
                  disabled={disabled}
                  aria-label="Insert emoji"
                  data-testid="button-emoji-picker"
                >
                  <Smile className="h-[18px] w-[18px]" />
                </Button>
              </PopoverTrigger>
            </Tooltip>
            <PopoverContent
              className="w-auto max-w-[calc(100vw-2rem)] p-0 border-none bg-popover shadow-bs1"
              side="top"
              align="start"
              sideOffset={10}
            >
              <EmojiPicker
                onEmojiClick={handleEmojiSelect}
                className={EMOJI_PICKER_THEME_CLASS_NAME}
                width="min(350px, calc(100vw - 2rem))"
                skinTonesDisabled
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
              />
            </PopoverContent>
          </Popover>

          {/* File Upload - Only show for WhatsApp and Chat */}
          {isAsync && (
            <>
              <Tooltip content={isUploading ? 'Uploading...' : 'Upload image'}>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-md border-st2"
                  onClick={handleAttachClick}
                  disabled={disabled || isUploading}
                  aria-label={isUploading ? 'Uploading attachment' : 'Upload image'}
                  data-testid="button-file-upload"
                >
                  {isUploading ? (
                    <Loader2 className="h-[18px] w-[18px] animate-spin" />
                  ) : (
                    <Paperclip className="h-[18px] w-[18px]" />
                  )}
                </Button>
              </Tooltip>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                accept="image/*"
              />
            </>
          )}
        </div>

        {/* Right side - Send button */}
        <div className="flex items-center">
          <Tooltip content="Send message">
            <Button
              size="icon"
              className="size-9 rounded-md"
              onClick={handleSendMessage}
              disabled={disabled || (!message.trim() && !uploadedFile) || isSending}
              aria-label={isSending ? 'Sending message' : 'Send message'}
              data-testid="button-send-message"
            >
              {isSending ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <ArrowUp className="h-5 w-5" />
              )}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
